import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, ChevronRight, X } from 'lucide-react';
import { callableFunctions } from '../firebase';
import '../styles/NetworkCascadeView.css';

// Vista en cascada de la red (estilo columnas de Finder): carga un nivel por vez, a demanda, en
// vez de traer todo el árbol de una. Por eso soporta líneas de profundidad infinita sin costo
// creciente — cada clic dispara una consulta chica ("hijos directos de esta persona").
const NetworkCascadeView = () => {
    const [columns, setColumns] = useState([]); // [{ parentId, parentLabel, children }]
    const [selectedPath, setSelectedPath] = useState([]); // id seleccionado por columna
    const [loadingColumnIndex, setLoadingColumnIndex] = useState(null);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [searchError, setSearchError] = useState(null);

    const scrollRef = useRef(null);

    const loadColumn = useCallback(async (parentUid, parentLabel, columnIndex) => {
        setLoadingColumnIndex(columnIndex);
        setError(null);
        try {
            const res = await callableFunctions.getNetworkChildren({ parentUid });
            const { children } = res.data;
            setColumns(prev => {
                const next = prev.slice(0, columnIndex);
                next.push({ parentId: parentUid, parentLabel, children });
                return next;
            });
            setSelectedPath(prev => prev.slice(0, columnIndex));
        } catch (err) {
            console.error('Error cargando la red:', err);
            setError(err.message || 'No se pudo cargar esta parte de tu red.');
        } finally {
            setLoadingColumnIndex(null);
        }
    }, []);

    useEffect(() => {
        loadColumn(null, 'Tu línea directa', 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({ left: scrollRef.current.scrollWidth, behavior: 'smooth' });
        }
    }, [columns.length]);

    const handleSelect = (member, columnIndex) => {
        setSelectedPath(prev => {
            const next = prev.slice(0, columnIndex);
            next[columnIndex] = member.id;
            return next;
        });
        if (member.hasChildren) {
            loadColumn(member.id, member.fullName, columnIndex + 1);
        } else {
            setColumns(prev => prev.slice(0, columnIndex + 1));
        }
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        const term = searchTerm.trim();
        if (term.length < 3) {
            setSearchError('Escribí al menos 3 caracteres para buscar.');
            return;
        }
        setSearching(true);
        setSearchError(null);
        try {
            const res = await callableFunctions.searchNetworkMembers({ query: term });
            setSearchResults(res.data.results);
        } catch (err) {
            console.error('Error buscando en la red:', err);
            setSearchError(err.message || 'No se pudo completar la búsqueda.');
            setSearchResults(null);
        } finally {
            setSearching(false);
        }
    };

    const openBranchFromSearch = (member) => {
        setSearchResults(null);
        setSearchTerm('');
        loadColumn(member.id, member.fullName, 0);
    };

    const closeSearch = () => {
        setSearchResults(null);
        setSearchError(null);
        setSearchTerm('');
    };

    return (
        <div className="network-cascade">
            <form className="network-search" onSubmit={handleSearchSubmit}>
                <Search size={16} className="network-search-icon" />
                <input
                    type="text"
                    placeholder="Buscar a alguien en toda tu red (nombre, usuario o email)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button type="submit" disabled={searching}>
                    {searching ? 'Buscando...' : 'Buscar'}
                </button>
            </form>

            {searchError && <p className="network-search-error">{searchError}</p>}

            {searchResults !== null && (
                <div className="network-search-results">
                    <div className="network-search-results-header">
                        <span>{searchResults.length} resultado{searchResults.length === 1 ? '' : 's'}</span>
                        <button type="button" onClick={closeSearch} className="network-search-close">
                            <X size={14} /> Cerrar
                        </button>
                    </div>
                    {searchResults.length === 0 ? (
                        <p className="empty-state-text">No encontramos a nadie con ese nombre, usuario o email en tu red.</p>
                    ) : (
                        searchResults.map(member => (
                            <div key={member.id} className="network-search-result-row" onClick={() => openBranchFromSearch(member)}>
                                <div>
                                    <p className="network-member-name">{member.fullName}</p>
                                    <p className="network-member-email">{member.userEmail}</p>
                                </div>
                                <span className="network-level-badge">Línea {member.level ?? '?'}</span>
                            </div>
                        ))
                    )}
                </div>
            )}

            {error && <div className="message-box error">{error}</div>}

            <div className="network-columns" ref={scrollRef}>
                {columns.map((col, colIdx) => (
                    <div key={`${col.parentId || 'root'}-${colIdx}`} className="network-column">
                        <div className="network-column-header">
                            <span>{col.parentLabel}</span>
                            <span className="network-column-count">{col.children.length}</span>
                        </div>
                        <div className="network-column-body">
                            {loadingColumnIndex === colIdx + 1 && (
                                <p className="empty-state-text">Cargando...</p>
                            )}
                            {col.children.length === 0 && (
                                <p className="empty-state-text">Sin referidos directos todavía.</p>
                            )}
                            {col.children.map(member => {
                                const isSelected = selectedPath[colIdx] === member.id;
                                return (
                                    <div
                                        key={member.id}
                                        className={`network-member-row ${isSelected ? 'selected' : ''}`}
                                        onClick={() => handleSelect(member, colIdx)}
                                    >
                                        <div className="network-member-main">
                                            <span className={`network-status-dot ${member.status === 'Activo' ? 'active' : 'inactive'}`} />
                                            <div className="network-member-text">
                                                <p className="network-member-name">{member.fullName}</p>
                                                <p className="network-member-email">{member.userEmail}</p>
                                            </div>
                                        </div>
                                        <div className="network-member-stats">
                                            <span className="network-member-volume">${(member.networkVolume || 0).toLocaleString('es-AR')}</span>
                                            <span className="network-member-size">{member.networkSize || 0} pers.</span>
                                            {member.hasChildren && <ChevronRight size={14} className="network-chevron" />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default NetworkCascadeView;
